// M6 boards/TV layer — GET /api/engage/board wire types + fetch-with-fallback.
// The endpoint is being built in parallel to a frozen shape: existing
// GET /api/leaderboard entry fields PLUS per-entry {percentile_band,
// is_pb_day, most_improved_pct}, plus top-level {most_improved, jobs}. Until
// (and if) it 404s for any reason, callers get the SAME shape back from the
// legacy leaderboard, normalized here, so BoardPage/TvBoard never branch on
// `source` for rendering — only to decide whether percentile bands are
// available. This is the one file both BoardPage.tsx and
// components/board/TvBoard.tsx import from.
import { api, readJson } from "./api";
import type { Job, LeaderboardHarvestLite, LeaderboardResponse, LeaderboardRow } from "./types";

export type EngageRow = LeaderboardRow & {
  percentile_band: string | null;
  is_pb_day: boolean;
  most_improved_pct: number | null;
};

export type MostImprovedEntry = { player_id: number; name: string; improvement_pct: number };

export type EngageJob = {
  id: number;
  name: string;
  progress_pct: number;
  eta_min: number | null;
  remaining_g: number;
  endgame: boolean;
};

export type EngageBoardResponse = {
  board: EngageRow[];
  harvests: LeaderboardHarvestLite[];
  harvest_id: number | null;
  most_improved: MostImprovedEntry[];
  jobs: EngageJob[];
};

export type BoardWindowKey = "today" | "7d" | "all";

export type BoardResult = { source: "engage" | "fallback"; data: EngageBoardResponse };

function jobToEngageJob(j: Job): EngageJob {
  return {
    id: j.id,
    name: j.name,
    progress_pct: j.pct,
    eta_min: j.eta_minutes,
    remaining_g: j.remaining_grams,
    endgame: j.pct >= 90 && j.status !== "done",
  };
}

/** Legacy /api/leaderboard's `days` param is a rolling window, not the
 * calendar-style enum the new endpoint takes — same mapping BoardPage.tsx
 * already used before this file existed. */
function legacyDaysFor(windowKey: BoardWindowKey): string {
  if (windowKey === "today") return "1";
  if (windowKey === "7d") return "7";
  return "";
}

/**
 * Fetches the addictive-layer board. Falls back to the plain
 * GET /api/leaderboard (+ GET /api/jobs for the TV jobs wall) ONLY on a 404
 * from /api/engage/board, per the brief — any other error is left to
 * surface normally rather than silently papered over.
 */
export async function fetchBoard(
  windowKey: BoardWindowKey, harvestId: string, siteId = "",
): Promise<BoardResult> {
  const qs = new URLSearchParams({ window: windowKey });
  if (harvestId) qs.set("harvest_id", harvestId);
  if (siteId) qs.set("site_id", siteId);
  const engageRes = await api.get(`/api/engage/board?${qs.toString()}`);

  if (engageRes.status !== 404) {
    const data = await readJson<EngageBoardResponse>(engageRes);
    return {
      source: "engage",
      data: {
        board: data.board ?? [],
        harvests: data.harvests ?? [],
        harvest_id: data.harvest_id ?? null,
        most_improved: data.most_improved ?? [],
        jobs: data.jobs ?? [],
      },
    };
  }

  const legacyQs = new URLSearchParams();
  const days = legacyDaysFor(windowKey);
  if (days) legacyQs.set("days", days);
  if (harvestId) legacyQs.set("harvest_id", harvestId);
  if (siteId) legacyQs.set("site_id", siteId);
  const legacy = await api.get(`/api/leaderboard?${legacyQs.toString()}`).then((r) => readJson<LeaderboardResponse>(r));

  // Best-effort only — the jobs wall just shows empty in fallback mode if
  // this fails, it's not worth failing the whole board over.
  let jobs: EngageJob[] = [];
  try {
    const rawJobs = await api.get("/api/jobs").then((r) => readJson<Job[]>(r));
    jobs = rawJobs.filter((j) => j.status !== "closed").map(jobToEngageJob);
  } catch {
    /* ignore */
  }

  return {
    source: "fallback",
    data: {
      board: legacy.board.map((r) => ({ ...r, percentile_band: null, is_pb_day: false, most_improved_pct: null })),
      harvests: legacy.harvests,
      harvest_id: legacy.harvest_id,
      most_improved: [],
      jobs,
    },
  };
}
